/* @img-migrated */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Users, Building2, Mail, Phone, Search, X, Star, Camera, Image as ImageIcon,
  RefreshCw, Edit3, Tag, Briefcase, Globe, MapPin, Calendar, CheckCircle2, Clock, Send, UserCheck, Loader2, Filter,
  Sparkles, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SortableTable from '../../components/SortableTable';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CONFIG = {
  pending:     { label: 'Pending',     className: 'bg-amber-100 text-amber-700 border-amber-200' },
  invite_sent: { label: 'Invite Sent', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  active:      { label: 'Active',      className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  activated:   { label: 'Active',      className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  expired:     { label: 'Expired',     className: 'bg-rose-100 text-rose-700 border-rose-200' },
  suspended:   { label: 'Suspended',   className: 'bg-slate-200 text-slate-700 border-slate-300' },
  approved:    { label: 'Approved',    className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const PLAN_COLORS = {
  Executive: 'bg-amber-100 text-amber-800 border-amber-200',
  Premier:   'bg-purple-100 text-purple-800 border-purple-200',
  Essential: 'bg-blue-100 text-blue-800 border-blue-200',
  Hybrid:    'bg-slate-100 text-slate-700 border-slate-200',
};

const TYPE_CONFIG = {
  business:   { label: 'Business',   className: 'bg-[#002249]/10 text-[#002249] border-[#002249]/20', icon: Building2 },
  individual: { label: 'Individual', className: 'bg-teal-100 text-teal-800 border-teal-200',        icon: Users },
  member:     { label: 'Member',     className: 'bg-slate-100 text-slate-700 border-slate-200',      icon: Users },
};

const INDUSTRY_OPTIONS = [
  'restaurants', 'professional', 'health', 'real_estate', 'finance', 'marketing',
  'technology', 'education', 'retail', 'community', 'other',
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status || '—', className: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${cfg.className}`}>{cfg.label}</span>;
};

const PlanBadge = ({ name }) => {
  if (!name) return <span className="text-slate-300">—</span>;
  const cls = PLAN_COLORS[name] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${cls}`}>{name}</span>;
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.member;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${cfg.className}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const MemberCenterPage = () => {
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [editModal, setEditModal] = useState(null); // member being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [togglingFeatured, setTogglingFeatured] = useState({});
  const logoInputRef = useRef(null);

  // Lock body scroll while edit modal is open
  useBodyScrollLock(!!editModal);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/members/full`, { withCredentials: true }),
        axios.get(`${API}/membership-plans`).catch(() => ({ data: [] })),
      ]);
      setMembers(mRes.data);
      setPlans(pRes.data);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (typeFilter !== 'all' && m.type !== typeFilter) return false;
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (planFilter !== 'all' && m.plan_name !== planFilter) return false;
      if (featuredOnly && !m.is_featured_homepage) return false;
      if (!q) return true;
      return (
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.company_name || '').toLowerCase().includes(q) ||
        (m.phone || '').toLowerCase().includes(q)
      );
    });
  }, [members, search, typeFilter, statusFilter, planFilter, featuredOnly]);

  const stats = useMemo(() => {
    const total = members.length;
    const business = members.filter((m) => m.type === 'business').length;
    const individual = members.filter((m) => m.type === 'individual').length;
    const active = members.filter((m) => m.status === 'active' || m.status === 'activated').length;
    const founding = members.filter((m) => m.is_founding_member).length;
    const featured = members.filter((m) => m.is_featured_homepage).length;
    return { total, business, individual, active, founding, featured };
  }, [members]);

  const toggleFeatured = async (member, e) => {
    if (e) e.stopPropagation();
    if (member.type !== 'business') {
      toast.error('Only business members can be featured on the homepage');
      return;
    }
    if (!member.logo_url && !member.is_featured_homepage) {
      // Allow turning OFF, but warn before turning ON without a logo
      toast.error('Upload a company logo before featuring this business');
      return;
    }
    setTogglingFeatured((prev) => ({ ...prev, [member.id]: true }));
    try {
      await axios.put(
        `${API}/admin/company-profiles/${member.id}/featured`,
        { is_featured_homepage: !member.is_featured_homepage },
        { withCredentials: true }
      );
      toast.success(member.is_featured_homepage ? 'Removed from homepage' : 'Now featured on homepage');
      // Optimistic update
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, is_featured_homepage: !m.is_featured_homepage } : m));
    } catch (err) {
      toast.error('Failed to toggle featured');
    } finally {
      setTogglingFeatured((prev) => ({ ...prev, [member.id]: false }));
    }
  };

  const openEdit = (member) => {
    setEditModal(member);
    setEditForm({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      company_name: member.company_name || '',
      industry: member.industry || '',
      website: member.website || '',
      assigned_plan_id: member.plan_id || '',
      is_founding_member: !!member.is_founding_member,
      is_individual: !!member.is_individual || member.type === 'individual',
      membership_status: member.status === 'activated' ? 'active' : member.status,
      membership_expires_at: member.expiration_date || '',
      is_featured_homepage: !!member.is_featured_homepage,
    });
  };

  const closeEdit = () => {
    setEditModal(null);
    setEditForm({});
  };

  const handleSave = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/members/${editModal.id}/full`, editForm, { withCredentials: true });
      // Sync featured flag if changed (separate endpoint)
      if (!!editForm.is_featured_homepage !== !!editModal.is_featured_homepage) {
        try {
          await axios.put(
            `${API}/admin/company-profiles/${editModal.id}/featured`,
            { is_featured_homepage: !!editForm.is_featured_homepage },
            { withCredentials: true }
          );
        } catch {
          toast.error('Member saved, but failed to update Featured flag');
        }
      }
      toast.success('Member updated');
      await fetchData();
      closeEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editModal) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/members/${editModal.id}/logo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo updated');
      setEditModal({ ...editModal, logo_url: res.data.logo_url });
      await fetchData();
    } catch (err) {
      toast.error('Logo upload failed');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDownload = async (member, e) => {
    if (e) e.stopPropagation();
    if (!member?.id || !member?.logo_url) {
      toast.error('No logo on file');
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/members/${member.id}/logo/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Derive filename from Content-Disposition if present
      const disp = res.headers?.['content-disposition'] || '';
      const match = disp.match(/filename="?([^"]+)"?/);
      const filename = match
        ? match[1]
        : `${(member.company_name || 'logo').replace(/[^a-zA-Z0-9_-]+/g, '_')}.${(member.logo_url.split('.').pop() || 'png')}`;
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Logo downloaded');
    } catch (err) {
      const msg = err?.response?.status === 404
        ? 'Logo file not found in storage'
        : err?.response?.data?.detail || 'Failed to download logo';
      toast.error(msg);
    }
  };

  const columns = [
    {
      id: 'featured',
      label: '',
      sortable: true,
      accessor: (r) => r.is_featured_homepage ? 0 : 1, // featured first when asc
      render: (r) => {
        const isBiz = r.type === 'business';
        const busy = togglingFeatured[r.id];
        if (!isBiz) return <span className="text-slate-200">—</span>;
        return (
          <button
            onClick={(e) => toggleFeatured(r, e)}
            disabled={busy}
            title={r.is_featured_homepage ? 'Featured on homepage — click to remove' : 'Feature on homepage'}
            className={`p-1.5 rounded-md transition-all ${r.is_featured_homepage
              ? 'text-amber-500 hover:bg-amber-50'
              : 'text-slate-300 hover:text-amber-400 hover:bg-slate-50'}`}
            data-testid={`featured-toggle-${r.id}`}
            data-featured={r.is_featured_homepage ? 'true' : 'false'}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Star className={`w-4 h-4 ${r.is_featured_homepage ? 'fill-amber-500' : ''}`} />
            )}
          </button>
        );
      },
      width: '44px',
      align: 'center',
    },
    {
      id: 'member',
      label: 'Member',
      accessor: (r) => (r.type === 'business' ? (r.company_name || r.name) : r.name) || '',
      render: (r) => {
        const isBiz = r.type === 'business';
        // Strict visual separation: businesses → logo (no initials fallback for name), individuals → profile photo
        if (isBiz) {
          return (
            <div className="flex items-center gap-2.5 min-w-[180px]">
              {r.logo_url ? (
                <Img variant="business" src={resolveImg(r.logo_url)} alt={r.company_name} className="w-9 h-9 rounded-md object-contain bg-slate-50 p-0.5 border border-slate-200 flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-md bg-slate-50 border border-dashed border-slate-300 text-slate-400 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#050505] truncate">{r.company_name || r.name || '—'}</p>
                {r.name && r.company_name && <p className="text-[11px] text-slate-500 truncate">{r.name}</p>}
                {r.is_founding_member && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium">
                    <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Founding
                  </span>
                )}
              </div>
            </div>
          );
        }
        // Individual member
        return (
          <div className="flex items-center gap-2.5 min-w-[180px]">
            {r.picture ? (
              <Img variant="avatar" src={resolveImg(r.picture)} alt={r.name} className="w-9 h-9 rounded-full object-cover bg-slate-100 flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white font-semibold text-[12px] flex items-center justify-center flex-shrink-0">
                {(r.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-[#050505] truncate">{r.name || '—'}</p>
              {r.leadership_title && <p className="text-[11px] text-teal-700 truncate">{r.leadership_title}</p>}
              {r.is_founding_member && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium">
                  <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Founding
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'type',
      label: 'Type',
      accessor: (r) => r.type || 'member',
      render: (r) => <TypeBadge type={r.type} />,
      width: '110px',
    },
    {
      id: 'company',
      label: 'Company',
      accessor: (r) => r.company_name || '',
      render: (r) => r.company_name ? (
        <span className="inline-flex items-center gap-1 text-[13px] text-slate-700">
          <Building2 className="w-3 h-3 text-slate-400" />{r.company_name}
        </span>
      ) : <span className="text-slate-300">—</span>,
    },
    {
      id: 'email',
      label: 'Email',
      accessor: (r) => r.email || '',
      render: (r) => r.email ? (
        <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="text-[13px] text-[#002249] hover:underline">{r.email}</a>
      ) : <span className="text-slate-300">—</span>,
    },
    {
      id: 'phone',
      label: 'Phone',
      accessor: (r) => r.phone || '',
      width: '140px',
    },
    {
      id: 'plan',
      label: 'Plan',
      accessor: (r) => r.plan_name || '',
      render: (r) => <PlanBadge name={r.plan_name} />,
      width: '110px',
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (r) => r.status || '',
      render: (r) => <StatusBadge status={r.status} />,
      width: '110px',
    },
    {
      id: 'industry',
      label: 'Industry',
      accessor: (r) => r.industry || '',
      width: '130px',
    },
    {
      id: 'join_date',
      label: 'Join Date',
      accessor: (r) => r.join_date || '',
      render: (r) => <span className="text-[12.5px] text-slate-500">{formatDate(r.join_date)}</span>,
      width: '110px',
    },
    {
      id: 'expiration_date',
      label: 'Expires',
      accessor: (r) => r.expiration_date || '',
      render: (r) => <span className="text-[12.5px] text-slate-500">{formatDate(r.expiration_date) || <span className="text-slate-300">—</span>}</span>,
      width: '110px',
    },
    {
      id: 'actions',
      label: '',
      sortable: false,
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {r.type === 'business' && r.logo_url && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => handleLogoDownload(r, e)}
              className="h-8 w-8 p-0 text-slate-500 hover:text-[#002249] hover:bg-[#002249]/5"
              title="Download company logo"
              data-testid={`download-logo-${r.id}`}
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); openEdit(r); }}
            className="h-8 px-2.5 text-[11.5px]"
            data-testid={`edit-member-${r.id}`}
          >
            <Edit3 className="w-3 h-3 mr-1" />Edit
          </Button>
        </div>
      ),
      width: '130px',
      align: 'right',
    },
  ];

  const uniqueStatuses = useMemo(() => Array.from(new Set(members.map((m) => m.status))).filter(Boolean), [members]);
  const uniquePlans = useMemo(() => Array.from(new Set(members.map((m) => m.plan_name))).filter(Boolean), [members]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid="member-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#002249] tracking-tight">Member Center</h1>
          <p className="text-sm text-slate-500 mt-1">
            Full membership workspace — edit member & business info, update plans, manage logos, track expirations.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          className="gap-2 h-10"
          data-testid="member-center-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {[
          { label: 'Total', value: stats.total, color: 'text-[#002249]' },
          { label: 'Business', value: stats.business, color: 'text-[#002249]' },
          { label: 'Individual', value: stats.individual, color: 'text-teal-700' },
          { label: 'Active', value: stats.active, color: 'text-emerald-700' },
          { label: 'Featured', value: stats.featured, color: 'text-amber-600' },
          { label: 'Founding', value: stats.founding, color: 'text-amber-700' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color} mt-0.5`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Quick Tabs — semantic grouping */}
      <div className="flex items-center gap-1.5 mb-3 overflow-x-auto scrollbar-hide" data-testid="mc-quick-tabs">
        {[
          { id: 'all',        label: 'All Members',    match: () => { setTypeFilter('all');        setStatusFilter('all'); setFeaturedOnly(false); } },
          { id: 'individual', label: 'Individuals',    match: () => { setTypeFilter('individual'); setStatusFilter('all'); setFeaturedOnly(false); } },
          { id: 'business',   label: 'Businesses',     match: () => { setTypeFilter('business');   setStatusFilter('all'); setFeaturedOnly(false); } },
          { id: 'featured',   label: 'Featured',       match: () => { setTypeFilter('business');   setStatusFilter('all'); setFeaturedOnly(true); } },
          { id: 'active',     label: 'Active',         match: () => { setTypeFilter('all');        setStatusFilter('active'); setFeaturedOnly(false); } },
          { id: 'pending',    label: 'Pending',        match: () => { setTypeFilter('all');        setStatusFilter('pending'); setFeaturedOnly(false); } },
          { id: 'expired',    label: 'Expired',        match: () => { setTypeFilter('all');        setStatusFilter('expired'); setFeaturedOnly(false); } },
        ].map((t) => {
          const isActive =
            (t.id === 'all' && typeFilter === 'all' && statusFilter === 'all' && !featuredOnly) ||
            (t.id === 'individual' && typeFilter === 'individual') ||
            (t.id === 'business' && typeFilter === 'business' && !featuredOnly) ||
            (t.id === 'featured' && featuredOnly) ||
            (t.id === 'active' && statusFilter === 'active') ||
            (t.id === 'pending' && statusFilter === 'pending') ||
            (t.id === 'expired' && statusFilter === 'expired');
          const count = t.id === 'featured'
            ? stats.featured
            : t.id === 'business'
              ? stats.business
              : t.id === 'individual'
                ? stats.individual
                : t.id === 'active'
                  ? stats.active
                  : t.id === 'all'
                    ? stats.total
                    : null;
          return (
            <button
              key={t.id}
              onClick={t.match}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-all ${
                isActive ? 'bg-[#002249] text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`mc-tab-${t.id}`}
            >
              {t.id === 'featured' && <Star className={`w-3.5 h-3.5 ${isActive ? 'fill-white' : 'fill-amber-400 text-amber-400'}`} />}
              {t.label}
              {count != null && (
                <span className={`text-[10.5px] font-semibold ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-4 flex flex-col lg:flex-row gap-2.5">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, company, phone..."
            className="pl-9 h-10 text-[14px]"
            data-testid="member-center-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
          data-testid="member-center-type-filter"
        >
          <option value="all">All Types</option>
          <option value="business">Business</option>
          <option value="individual">Individual</option>
          <option value="member">Member</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
          data-testid="member-center-status-filter"
        >
          <option value="all">All Statuses</option>
          {uniqueStatuses.map((s) => (
            <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
          ))}
        </select>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
          data-testid="member-center-plan-filter"
        >
          <option value="all">All Plans</option>
          {uniquePlans.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-16 flex items-center justify-center border border-slate-200">
          <Loader2 className="w-8 h-8 animate-spin text-[#002249]" />
        </div>
      ) : (
        <SortableTable
          columns={columns}
          data={filtered}
          defaultSort={{ id: 'member', direction: 'asc' }}
          onRowClick={openEdit}
          rowTestId={(r) => `member-row-${r.id}`}
          emptyMessage={search || typeFilter !== 'all' || statusFilter !== 'all' || planFilter !== 'all'
            ? 'No members match your filters.'
            : 'No members yet.'}
        />
      )}
      <p className="text-[11.5px] text-slate-400 mt-2 italic">
        Click any column header to sort. Click again to reverse. Click a third time to clear.
      </p>

      {/* EDIT MODAL */}
      <ModalPortal>
        <AnimatePresence>
          {editModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={closeEdit}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full sm:max-w-[720px] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
                data-testid="member-edit-modal"
              >
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  {editModal.logo_url ? (
                    <Img variant="business" src={resolveImg(editModal.logo_url)} alt="" className="w-10 h-10 rounded-lg object-contain bg-slate-50 p-1 border border-slate-200" />
                  ) : editModal.picture ? (
                    <Img variant="avatar" src={resolveImg(editModal.picture)} alt="" className="w-10 h-10 rounded-full object-cover bg-slate-100" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold">
                      {(editModal.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-[15px] font-bold text-[#002249] truncate">{editModal.name || 'Edit Member'}</h3>
                    <p className="text-[11px] text-slate-500 truncate">{editModal.email}</p>
                  </div>
                </div>
                <button onClick={closeEdit} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500" data-testid="member-edit-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body */}
              <div className="overflow-y-auto px-5 py-5 space-y-5 flex-1">
                {/* Personal info */}
                <section>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Personal Info</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Full Name">
                      <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                    </Field>
                    <Field label="Email">
                      <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                    </Field>
                    <Field label="Phone">
                      <Input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                    </Field>
                    <Field label="Account Type">
                      <select
                        value={editForm.is_individual ? 'individual' : 'business'}
                        onChange={(e) => setEditForm({ ...editForm, is_individual: e.target.value === 'individual' })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px]"
                      >
                        <option value="business">Business</option>
                        <option value="individual">Individual</option>
                      </select>
                    </Field>
                  </div>
                </section>

                {/* Business info */}
                <section>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
                    <span>Business Info</span>
                    {editForm.is_individual && <span className="text-[10.5px] italic normal-case text-slate-400 font-normal">Optional for individuals</span>}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Company Name">
                      <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
                    </Field>
                    <Field label="Industry">
                      <select
                        value={editForm.industry}
                        onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px]"
                      >
                        <option value="">— Select —</option>
                        {INDUSTRY_OPTIONS.map((i) => <option key={i} value={i}>{i.replace('_', ' ')}</option>)}
                      </select>
                    </Field>
                    <Field label="Website">
                      <Input type="url" placeholder="https://..." value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
                    </Field>
                    <Field label="Company Logo">
                      <div className="flex items-center gap-2 flex-wrap">
                        {editModal.logo_url ? (
                          <Img variant="business" src={resolveImg(editModal.logo_url)} alt="" className="w-11 h-11 rounded-lg object-contain bg-slate-50 p-1 border border-slate-200 flex-shrink-0" />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="gap-1 h-10"
                          data-testid="member-upload-logo"
                        >
                          {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                          {editModal.logo_url ? 'Replace' : 'Upload'}
                        </Button>
                        {editModal.logo_url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleLogoDownload(editModal)}
                            className="gap-1 h-10 border-[#002249]/30 text-[#002249] hover:bg-[#002249]/5"
                            data-testid="member-download-logo"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </Button>
                        )}
                        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                      </div>
                    </Field>
                  </div>
                </section>

                {/* Membership */}
                <section>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Membership</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Plan">
                      <select
                        value={editForm.assigned_plan_id}
                        onChange={(e) => setEditForm({ ...editForm, assigned_plan_id: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px]"
                      >
                        <option value="">— No plan —</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Status">
                      <select
                        value={editForm.membership_status}
                        onChange={(e) => setEditForm({ ...editForm, membership_status: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px]"
                      >
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="invite_sent">Invite Sent</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </Field>
                    <Field label="Expiration Date">
                      <Input
                        type="date"
                        value={editForm.membership_expires_at ? editForm.membership_expires_at.slice(0, 10) : ''}
                        onChange={(e) => setEditForm({ ...editForm, membership_expires_at: e.target.value })}
                      />
                    </Field>
                    <Field label="Founding Member">
                      <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!editForm.is_founding_member}
                          onChange={(e) => setEditForm({ ...editForm, is_founding_member: e.target.checked })}
                          className="w-4 h-4 accent-[#002249]"
                          data-testid="edit-founding-checkbox"
                        />
                        <span className="text-[13px]">Mark as founding member</span>
                      </label>
                    </Field>
                    {!editForm.is_individual && (
                      <Field label="Homepage Feature (⭐)">
                        <label
                          className={`flex items-center gap-2 h-10 px-3 rounded-md border text-[14px] cursor-pointer transition-colors ${
                            editForm.is_featured_homepage
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-white border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!editForm.is_featured_homepage}
                            onChange={(e) => setEditForm({ ...editForm, is_featured_homepage: e.target.checked })}
                            className="w-4 h-4 accent-amber-500"
                            data-testid="edit-featured-checkbox"
                          />
                          <Star className={`w-3.5 h-3.5 ${editForm.is_featured_homepage ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
                          <span className="text-[13px]">Show in Homepage "Featured Companies"</span>
                        </label>
                        {!editModal.logo_url && (
                          <p className="text-[10.5px] text-amber-700 mt-1 italic flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Upload a company logo to publish on the homepage
                          </p>
                        )}
                      </Field>
                    )}
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                <Button variant="outline" onClick={closeEdit} disabled={saving} data-testid="member-edit-cancel">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#001630] gap-1.5" data-testid="member-edit-save">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Changes
                </Button>
              </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalPortal>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11.5px] font-semibold text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

export default MemberCenterPage;
